import { formatAppDate } from '../utility/dateLocale';
import { PageRecordFilters } from '../components/PageRecordFilters';
import { InvoiceMonthFilter } from '../components/InvoiceMonthFilter';
import { matchesPageMonth } from '../utility/pageMonthFilter';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { buildMrtLocalization } from '../i18n/mrtLocale';
import {
  Modal,
  Button,
  TextInput,
  Select,
  Group,
  Title,
  Text,
  NumberInput,
  ScrollArea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { type MRT_ColumnDef, type MRT_Row } from 'mantine-react-table';
import { ExportableMantineReactTable, useExportableTable } from '../utility/useExportableTable';
import { enableGlobalSearchOnColumns } from '../utility/mrtTableSearch';
import { ImportConfirmModal } from '../components/ImportConfirmModal';
import { TableExcelImportModal } from '../components/TableExcelImportModal';
import { classifyImportRows } from '../utility/excelDeletedColumn';
import { executeImportUpsert } from '../utility/executeImportUpsert';
import {
  formKeysPresentInRow,
  mergeDefinedFields,
  readRowCell,
} from '../utility/excelImportHelpers';
import { readExcelFile, splitImportRowsById } from '../utility/tableExcelExport';
import { MRT_DensityState } from 'mantine-react-table';
import Environment from '../utility/environment';
import { useAuth } from '../contexts/AuthProvider';
import { Options } from '../utility/types';
import { useMrtTableContainerProps, useModalScrollAreaHeight, usePageTableShellStyle } from '../utility/mobilePageLayout';

type OpportunityOption = Options & { client_ids: string[] };

type InvoiceOption = {
  id: string;
  client_id?: string;
  issue_date: string;
  total_amount: string;
  remaining_amount: string;
  currency?: string;
  status: string;
  invoice_number?: number;
  invoice_total_count?: number;
};

function formatInvoiceAmount(raw: string, currency?: string) {
  const n = parseFloat(raw);
  if (Number.isNaN(n)) return raw;
  const formatted = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${formatted}` : formatted;
}

type Balance = {
  id: number;
  uuid: string;
  transaction_date: string;
  posting_date: string | null;
  description: string | null;
  category: string | null;
  sub_category: string | null;
  account: string | null;
  payment_method: string | null;
  client_id: string | null;
  supplier_id: string | null;
  opportunity_id: string | null;
  invoice_id: string | null;
  debit: number | null;
  credit: number | null;
  amount: number;
  currency: string | null;
  status: string;
  notes: string | null;
  deletedAt?: string | null;
};

type BalanceForm = Omit<Balance, 'id' | 'uuid'>;

const initialForm: BalanceForm = {
  transaction_date: new Date(Date.now()).toISOString().split('T')[0],
  posting_date: null,
  description: '',
  category: null,
  sub_category: '',
  account: null,
  payment_method: '',
  client_id: null,
  supplier_id: null,
  opportunity_id: null,
  invoice_id: null,
  debit: null,
  credit: null,
  amount: 0,
  currency: 'USD',
  status: 'pending',
  notes: '',
};

const currencyOptions = [
  { value: 'USD', label: 'USD' },
  { value: 'ARS', label: 'ARS' },
];

export default function BalancePage() {
  const { t, i18n } = useTranslation();
  const { accessToken, actualEnterprise, user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [statusFilter,setStatusFilter]=useState<string[]>([]);
  const [clientFilter,setClientFilter]=useState<string[]>([]);
  const [opportunityFilter,setOpportunityFilter]=useState<string[]>([]);
  const [categoryFilter,setCategoryFilter]=useState<string[]>([]);
  const [currencyFilter,setCurrencyFilter]=useState<string[]>([]);
  const [minFilter,setMinFilter]=useState<number | ''>('');
  const [maxFilter,setMaxFilter]=useState<number | ''>('');
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();
  const modalScrollAreaHeight = useModalScrollAreaHeight();

  const mrtLocalization = useMemo(
    () => buildMrtLocalization(t),
    [t, i18n.language],
  );

  const categoryOptions = useMemo(
    () => [
      { value: 'revenue', label: t('balance.categoryOptions.revenue') },
      { value: 'expense', label: t('balance.categoryOptions.expense') },
      { value: 'asset', label: t('balance.categoryOptions.asset') },
      { value: 'liability', label: t('balance.categoryOptions.liability') },
    ],
    [t, i18n.language],
  );

  const accountOptions = useMemo(
    () => [
      { value: 'bank', label: t('balance.accountOptions.bank') },
      { value: 'credit_card', label: t('balance.accountOptions.credit_card') },
      { value: 'cash', label: t('balance.accountOptions.cash') },
    ],
    [t, i18n.language],
  );

  const statusOptions = useMemo(
    () => [
      { value: 'pending', label: t('balance.statusOptions.pending') },
      { value: 'paid', label: t('balance.statusOptions.paid') },
      { value: 'cleared', label: t('balance.statusOptions.cleared') },
      { value: 'accepted', label: t('balance.statusOptions.accepted') },
    ],
    [t, i18n.language],
  );

  const [balances, setBalances] = useState<Balance[]>([]);
  const [clients, setClients] = useState<Options[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityOption[]>([]);
  const [invoiceOptionsByOpportunity, setInvoiceOptionsByOpportunity] = useState<Record<string, InvoiceOption[]>>({});

  const [modalOpened, setModalOpened] = useState(false);
  const [editingBalance, setEditingBalance] = useState<Balance | null>(null);
  const [isEditingBalance, setIsEditingBalance] = useState(false);
  const [balanceForm, setBalanceForm] = useState<BalanceForm>(initialForm);
  const [errorMessage, setErrorMessage] = useState('');
  const [notification, setNotification] = useState<{ message: string; color: 'green' | 'red' } | null>(null);
  const [confirmOverpayOpened, setConfirmOverpayOpened] = useState(false);
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importModalOpened, setImportModalOpened] = useState(false);
  const [importConfirm, setImportConfirm] = useState<{
    newCount: number;
    updateCount: number;
    deleteCount: number;
    run: () => Promise<void>;
  } | null>(null);

  useEffect(() => {
    getBalances();
    getClients();
    getOpportunities();
  }, [accessToken, actualEnterprise]);

  useEffect(() => {
    document.title = t('balance.documentTitle');
  }, [t, i18n.language]);

  const isFormValid = () => {
    if (balanceForm.transaction_date.trim() === '') return false;
    if (balanceForm.amount <= 0) return false;
    return true;
  };

  const opportunitiesForClient = useMemo(() => {
    if (!balanceForm.client_id) return [];
    return opportunities
      .filter((o) => o.client_ids.includes(balanceForm.client_id!))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [balanceForm.client_id, opportunities]);

  const invoicesForOpportunity = useMemo(() => {
    if (!balanceForm.opportunity_id) return [];
    const options = invoiceOptionsByOpportunity[balanceForm.opportunity_id] ?? [];
    const filtered = balanceForm.client_id
      ? options.filter((inv) => inv.client_id === balanceForm.client_id)
      : options;
    return filtered.map((inv) => ({
      value: inv.id,
      label: t('balance.invoiceOptionLabel', {
        number: inv.invoice_number,
        total: inv.invoice_total_count,
        date: formatAppDate(inv.issue_date),
        totalAmount: formatInvoiceAmount(inv.total_amount, inv.currency),
        remaining: formatInvoiceAmount(inv.remaining_amount, inv.currency),
      }),
    }));
  }, [balanceForm.client_id, balanceForm.opportunity_id, invoiceOptionsByOpportunity, t, i18n.language]);

  const maxAmountForInvoice = useMemo<number | undefined>(() => {
    if (!balanceForm.invoice_id || !balanceForm.opportunity_id) return undefined;
    const options = invoiceOptionsByOpportunity[balanceForm.opportunity_id] ?? [];
    const inv = options.find((o) => o.id === balanceForm.invoice_id);
    if (!inv) return undefined;
    let remaining = parseFloat(inv.remaining_amount);
    if (editingBalance?.invoice_id === balanceForm.invoice_id) {
      remaining += editingBalance.amount;
    }
    return Math.max(remaining, 0);
  }, [balanceForm.invoice_id, balanceForm.opportunity_id, invoiceOptionsByOpportunity, editingBalance]);

  const exceedsInvoiceRemaining =
    maxAmountForInvoice != null && balanceForm.amount > maxAmountForInvoice;

  const openModal = (balance?: Balance) => {
    if (balance) {
      setEditingBalance(balance);
      setBalanceForm({
        transaction_date: balance.transaction_date,
        posting_date: balance.posting_date,
        description: balance.description || '',
        category: balance.category,
        sub_category: balance.sub_category || '',
        account: balance.account,
        payment_method: balance.payment_method || '',
        client_id: balance.client_id,
        supplier_id: balance.supplier_id,
        opportunity_id: balance.opportunity_id,
        invoice_id: balance.invoice_id,
        debit: balance.debit,
        credit: balance.credit,
        amount: balance.amount,
        currency: balance.currency || 'USD',
        status: balance.status,
        notes: balance.notes || '',
      });
    } else {
      setEditingBalance(null);
      setBalanceForm(initialForm);
    }
    setIsEditingBalance(false);
    setModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setErrorMessage('');
    setNotification(null);
  };

  const getBalances = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/balances/${actualEnterprise?.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const balanceList = data.balances ?? data;
        const formattedBalances: Balance[] = balanceList.map((b: any, index: number) => ({
          id: index + 1,
          uuid: b.id,
          transaction_date: b.transaction_date ? new Date(b.transaction_date).toISOString().split('T')[0] : '',
          posting_date: b.posting_date ? new Date(b.posting_date).toISOString().split('T')[0] : null,
          description: b.description,
          category: b.category,
          sub_category: b.sub_category,
          account: b.account,
          payment_method: b.payment_method,
          client_id: b.client_id,
          supplier_id: b.supplier_id,
          opportunity_id: b.opportunity_id,
          invoice_id: b.invoice_id ?? null,
          debit: b.debit != null ? parseFloat(b.debit) : null,
          credit: b.credit != null ? parseFloat(b.credit) : null,
          amount: parseFloat(b.amount),
          currency: b.currency,
          status: b.status || 'pending',
          notes: b.notes,
          deletedAt: b.deleted_at ?? null,
        }));
        setBalances(formattedBalances);
        if (data.invoice_options) {
          setInvoiceOptionsByOpportunity(data.invoice_options);
        }
      })
      .catch((err) => console.error('Error fetching balances:', err));
  };

  const getClients = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/clients_options/${actualEnterprise?.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const options: Options[] = data.map((c: { id: string; legal_name: string }) => ({
          value: c.id,
          label: c.legal_name,
        }));
        setClients(options.sort((a, b) => a.label.localeCompare(b.label)));
      })
      .catch((err) => console.error('Error fetching clients:', err));
  };

  const getOpportunities = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/valid_opportunities/${actualEnterprise?.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const options: OpportunityOption[] = data.map((o: { id: string; name: string; client_ids: string[] }) => ({
          value: o.id,
          label: o.name,
          client_ids: o.client_ids ?? [],
        }));
        setOpportunities(options);
      })
      .catch((err) => console.error('Error fetching opportunities:', err));
  };

  const handleClientChange = (value: string | null) => {
    setBalanceForm((prev) => {
      const clientChanged = prev.client_id !== value;
      return {
        ...prev,
        client_id: value,
        opportunity_id: clientChanged ? null : prev.opportunity_id,
        invoice_id: clientChanged ? null : prev.invoice_id,
      };
    });
  };

  const handleOpportunityChange = (value: string | null) => {
    setBalanceForm((prev) => ({
      ...prev,
      opportunity_id: value,
      invoice_id: null,
    }));
  };

  const deleteBalance = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/balance/${editingBalance?.uuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then(async (response) => {
        if (!response.ok) {
          const err = await response.json();
          setNotification({ message: err.detail || t('balance.deleteFailed'), color: 'red' });
          setErrorMessage(err.detail || t('balance.deleteFailed'));
        }
        if (response.status === 200) {
          setModalOpened(false);
          getBalances();
        }
      })
      .catch(() => {
        setModalOpened(true);
      });
  };

  const updateBalance = () => {
    const payload = formatBalance(balanceForm);
    fetch(`${Environment.VITE_API_URL}/api/v1/balances/${editingBalance?.uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then(() => {})
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setModalOpened(false);
        getBalances();
      });
  };

  const createBalance = () => {
    const payload = formatBalance(balanceForm);
    fetch(`${Environment.VITE_API_URL}/api/v1/balances/${actualEnterprise?.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then(() => {})
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setModalOpened(false);
        getBalances();
      });
  };

  const formatBalance = (form: BalanceForm) => {
    const payload: Record<string, unknown> = {
      transaction_date: form.transaction_date,
      posting_date: form.posting_date || undefined,
      description: form.description || undefined,
      category: form.category || undefined,
      sub_category: form.sub_category || undefined,
      account: form.account || undefined,
      payment_method: form.payment_method || undefined,
      supplier_id: form.supplier_id || undefined,
      debit: form.debit != null ? Number(form.debit) : undefined,
      credit: form.credit != null ? Number(form.credit) : undefined,
      amount: Number(form.amount),
      currency: form.currency || undefined,
      status: form.status || undefined,
      notes: form.notes || undefined,
    };
    if (form.client_id) payload.client_id = form.client_id;
    if (form.opportunity_id) payload.opportunity_id = form.opportunity_id;
    if (form.invoice_id) payload.invoice_id = form.invoice_id;
    return payload;
  };

  const balanceImportHeaders = useMemo(
    () => ({
      transaction_date: t('balance.transactionDate'),
      posting_date: t('balance.postingDate'),
      description: t('balance.description'),
      category: t('balance.category'),
      sub_category: t('balance.subCategory'),
      account: t('balance.account'),
      payment_method: t('balance.paymentMethod'),
      client_id: t('balance.client'),
      opportunity_id: t('balance.opportunity'),
      invoice_id: t('balance.invoice'),
      amount: t('balance.amount'),
      currency: t('balance.currency'),
      status: t('common.status'),
      notes: t('balance.notes'),
    }),
    [t, i18n.language],
  );

  const resolveOptionValue = (
    raw: unknown,
    options: { value: string; label: string }[],
  ): string | null => {
    const text = String(raw ?? '').trim();
    if (!text) return null;
    const byValue = options.find((option) => option.value === text);
    if (byValue) return byValue.value;
    const byLabel = options.find((option) => option.label.toLowerCase() === text.toLowerCase());
    return byLabel?.value ?? text;
  };

  const rowToBalanceForm = (row: Record<string, unknown>): BalanceForm => ({
    transaction_date: String(
      readRowCell(row, balanceImportHeaders.transaction_date, 'Transaction Date') ?? '',
    ),
    posting_date: readRowCell(row, balanceImportHeaders.posting_date, 'Posting Date')
      ? String(readRowCell(row, balanceImportHeaders.posting_date, 'Posting Date'))
      : null,
    description: String(readRowCell(row, balanceImportHeaders.description, 'Description') ?? ''),
    category: resolveOptionValue(readRowCell(row, balanceImportHeaders.category, 'Category'), categoryOptions),
    sub_category: String(readRowCell(row, balanceImportHeaders.sub_category, 'Sub-Category') ?? ''),
    account: resolveOptionValue(readRowCell(row, balanceImportHeaders.account, 'Account'), accountOptions),
    payment_method: String(readRowCell(row, balanceImportHeaders.payment_method, 'Payment Method') ?? ''),
    client_id: readRowCell(row, balanceImportHeaders.client_id, 'Client ID', 'client_id')
      ? String(readRowCell(row, balanceImportHeaders.client_id, 'Client ID', 'client_id'))
      : null,
    supplier_id: null,
    opportunity_id: readRowCell(row, balanceImportHeaders.opportunity_id, 'Opportunity ID', 'opportunity_id')
      ? String(readRowCell(row, balanceImportHeaders.opportunity_id, 'Opportunity ID', 'opportunity_id'))
      : null,
    invoice_id: readRowCell(row, balanceImportHeaders.invoice_id, 'Invoice ID', 'invoice_id')
      ? String(readRowCell(row, balanceImportHeaders.invoice_id, 'Invoice ID', 'invoice_id'))
      : null,
    debit: null,
    credit: null,
    amount: Number(readRowCell(row, balanceImportHeaders.amount, 'Amount') ?? 0),
    currency: String(readRowCell(row, balanceImportHeaders.currency, 'Currency') ?? 'USD'),
    status: resolveOptionValue(readRowCell(row, balanceImportHeaders.status, 'Status'), statusOptions) ?? 'pending',
    notes: String(readRowCell(row, balanceImportHeaders.notes, 'Notes') ?? ''),
  });

  const validateBalanceForm = (form: BalanceForm, line: number): string[] => {
    const rowErrors: string[] = [];
    if (!form.transaction_date.trim()) rowErrors.push(t('balance.transactionDate'));
    if (!form.amount || form.amount <= 0) rowErrors.push(t('balance.amount'));
    if (rowErrors.length > 0) {
      return [t('common.import.rowError', { line, errors: rowErrors.join(' ') })];
    }
    return [];
  };

  const executeBalanceImport = async (
    createForms: BalanceForm[],
    updateEntries: Array<{ id: string; form: BalanceForm }>,
    deleteIds: string[],
  ) => {
    for (const form of createForms) {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/balances/${actualEnterprise?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(formatBalance(form)),
      });
      if (!response.ok) {
        setNotification({ message: t('common.import.failed'), color: 'red' });
        return;
      }
    }

    const ok = await executeImportUpsert({
      createPayloads: [],
      updateEntries: updateEntries.map((entry) => ({
        id: entry.id,
        payload: formatBalance(entry.form),
      })),
      deleteIds,
      updateUrl: (id) => `${Environment.VITE_API_URL}/api/v1/balances/${id}`,
      deleteUrl: (id) => `${Environment.VITE_API_URL}/api/v1/balance/${id}`,
      buildRequest: ({ method, body }) => ({
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    });

    if (!ok) {
      setNotification({ message: t('common.import.failed'), color: 'red' });
      return;
    }

    setNotification({ message: t('common.import.success'), color: 'green' });
    setModalOpened(false);
    setImportModalOpened(false);
    getBalances();
  };

  const handleAutoImport = async () => {
    if (!autoImportFile) return;
    try {
      const rawData = await readExcelFile(autoImportFile);
      const existingIds = new Set(balances.map((balance) => balance.uuid));
      const classified = classifyImportRows(rawData, existingIds, t);
      const split = splitImportRowsById(classified.rowsForUpsert, existingIds);
      const errors: string[] = [...classified.errors];

      const headerToFormKey = Object.fromEntries(
        Object.entries(balanceImportHeaders).map(([formKey, header]) => [header, formKey]),
      ) as Record<string, keyof BalanceForm & string>;

      const createForms: BalanceForm[] = [];
      for (const row of split.creates) {
        const line = rawData.indexOf(row) + 2;
        const form = rowToBalanceForm(row);
        errors.push(...validateBalanceForm(form, line));
        createForms.push(form);
      }

      const updateEntries: Array<{ id: string; form: BalanceForm }> = [];
      for (const entry of split.updates) {
        const line = rawData.indexOf(entry.row) + 2;
        const existing = balances.find((balance) => balance.uuid === entry.id);
        if (!existing) continue;
        const patch = rowToBalanceForm(entry.row);
        const merged = mergeDefinedFields(
          {
            transaction_date: existing.transaction_date,
            posting_date: existing.posting_date,
            description: existing.description ?? '',
            category: existing.category,
            sub_category: existing.sub_category ?? '',
            account: existing.account,
            payment_method: existing.payment_method ?? '',
            client_id: existing.client_id,
            supplier_id: existing.supplier_id,
            opportunity_id: existing.opportunity_id,
            invoice_id: existing.invoice_id,
            debit: existing.debit,
            credit: existing.credit,
            amount: existing.amount,
            currency: existing.currency ?? 'USD',
            status: existing.status,
            notes: existing.notes ?? '',
          },
          {
            ...patch,
            description: patch.description ?? '',
            sub_category: patch.sub_category ?? '',
            payment_method: patch.payment_method ?? '',
            notes: patch.notes ?? '',
            currency: patch.currency ?? 'USD',
          },
          formKeysPresentInRow(entry.row, headerToFormKey),
        );
        errors.push(...validateBalanceForm(merged, line));
        updateEntries.push({ id: entry.id, form: merged });
      }

      if (errors.length > 0) {
        setNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors(errors);
        return;
      }

      setImportConfirm({
        newCount: createForms.length,
        updateCount: updateEntries.length,
        deleteCount: classified.deleteIds.length,
        run: () => executeBalanceImport(createForms, updateEntries, classified.deleteIds),
      });
    } catch (error) {
      console.error('Error processing import file:', error);
      setNotification({ message: t('common.import.processFailed'), color: 'red' });
    }
  };

  const columns = useMemo<MRT_ColumnDef<Balance>[]>(
    () => enableGlobalSearchOnColumns(
      [
      {
        accessorKey: 'transaction_date',
        Cell: ({ cell }) => formatAppDate(cell.getValue<string>()),
        header: t('balance.transactionDate'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'opportunity_id',
        header: t('balance.opportunity'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
        Cell: ({ cell }) => {
          const oppId = cell.getValue<string>();
          if (!oppId) return '-';
          const opp = opportunities.find((o) => o.value === oppId);
          return opp?.label ?? oppId;
        },
      },
      {
        accessorKey: 'invoice_id',
        header: t('balance.invoice'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
        Cell: ({ cell, row }) => {
          const invoiceId = cell.getValue<string>();
          if (!invoiceId) return '-';
          const oppId = row.original.opportunity_id;
          const options = oppId ? invoiceOptionsByOpportunity[oppId] : [];
          const match = options?.find((o) => o.id === invoiceId);
          if (!match) return invoiceId;
          const prefix = match.invoice_number && match.invoice_total_count
            ? `(#${match.invoice_number} of ${match.invoice_total_count}) `
            : '';
          return `${prefix}${formatAppDate(match.issue_date)} — ${formatInvoiceAmount(match.total_amount, match.currency)}`;
        },
      },
      {
        accessorKey: 'description',
        header: t('balance.description'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'category',
        header: t('balance.category'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ cell }) => {
          const v = cell.getValue<string>();
          return v ? categoryOptions.find((o) => o.value === v)?.label ?? v : '-';
        },
      },
      {
        accessorKey: 'account',
        header: t('balance.account'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ cell }) => {
          const v = cell.getValue<string>();
          return v ? accountOptions.find((o) => o.value === v)?.label ?? v : '-';
        },
      },
      {
        accessorKey: 'amount',
        header: t('balance.amount'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'right' } },
        Cell: ({ cell }) => {
          const amount = cell.getValue<number>();
          const currency = cell.row.original.currency || 'USD';
          return `${currency} ${amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
      },
      {
        accessorKey: 'status',
        header: t('common.status'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ cell }) => {
          const v = cell.getValue<string>();
          return v ? statusOptions.find((o) => o.value === v)?.label ?? v : '-';
        },
      },
      {
        accessorKey: 'notes',
        header: t('balance.notes'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'client_id',
        header: t('balance.client'),
        enableHiding: true,
      },
      {
        accessorKey: 'opportunity_id',
        header: t('balance.opportunity'),
        enableHiding: true,
      },
      {
        accessorKey: 'invoice_id',
        header: t('balance.invoice'),
        enableHiding: true,
      },
    ],
      ['description', 'notes'],
    ),
    [opportunities, invoiceOptionsByOpportunity, categoryOptions, accountOptions, statusOptions, t, i18n.language]
  );

  const openImportModal = () => {
    setImportModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setNotification(null);
  };

  const filterCurrencyOptions = useMemo(()=>[
    {value:'all',label:t('common.recordFilters.allCurrencies')},
    ...Array.from(new Set([...currencyOptions.map(option=>option.value),...balances.map(balance=>balance.currency||'USD')])).sort().map(value=>({value,label:value})),
  ],[balances,t,i18n.language]);
  const rangeBounds = useMemo(()=>{
    const amounts=balances.filter(balance=>currencyFilter.length===1&&(balance.currency||'USD')===currencyFilter[0]).map(balance=>Number(balance.amount)).filter(Number.isFinite);
    const min=Math.min(0,...amounts.map(value=>Math.floor(value)));
    return {min,max:Math.max(min+1,...amounts.map(value=>Math.ceil(value)))};
  },[balances,currencyFilter]);
  const monthBalances = useMemo(()=>balances.filter(balance=>
    matchesPageMonth(balance.transaction_date,selectedMonth)
    &&(!statusFilter.length||statusFilter.includes(balance.status))
    &&(!clientFilter.length||clientFilter.includes(balance.client_id||''))
    &&(!opportunityFilter.length||opportunityFilter.includes(balance.opportunity_id||''))
    &&(!categoryFilter.length||categoryFilter.includes(balance.category||''))
    &&(!currencyFilter.length||currencyFilter.includes(balance.currency||'USD'))
    &&(user?.role!=='admin'||currencyFilter.length!==1||minFilter===''||balance.amount>=minFilter)
    &&(user?.role!=='admin'||currencyFilter.length!==1||maxFilter===''||balance.amount<=maxFilter)
  ),[balances,selectedMonth,statusFilter,clientFilter,opportunityFilter,categoryFilter,currencyFilter,minFilter,maxFilter,user?.role]);
  const { table, exportModal } = useExportableTable(
    {
      columns,
      data: monthBalances,
      localization: mrtLocalization,
      defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
      enableColumnFilters: true,
      enableSorting: true,
      enableFullScreenToggle: true,
      enableGrouping: false,
      enableColumnActions: false,
      enablePagination: true,
      enableBottomToolbar: true,
      enableRowVirtualization: false,
      initialState: {
        density: 'sm' as MRT_DensityState,
        showGlobalFilter: true,
        columnVisibility: {
          client_id: false,
          opportunity_id: false,
          invoice_id: false,
        },
        pagination: {
          pageSize: 20,
          pageIndex: 0,
        },
      },
      mantineTableContainerProps: mrtTableContainerProps,
      mantineTableProps: {
        sx: {
          display: 'table',
          tableLayout: 'auto',
          borderCollapse: 'separate',
          '& .mantine-TableHeadCell-Content': {
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1px 1px',
          },
          '& .mantine-TextInput-input': {
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '4px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#000',
          },
          '& th, & td': {
            borderRadius: '4px',
          },
          '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'rgba(48, 113, 91, 0.9)',
            color: '#fff',
          },
        },
      },
      mantineTableBodyRowProps: ({ row }: { row: MRT_Row<Balance> }) => ({
        onDoubleClick: () => handleRowDoubleClick(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    {
      idField: 'uuid',
      filenamePrefix: 'balance',
      sheetName: 'Balance',
      getDeletedValue: (row) => Boolean(row.deletedAt),
      getCellValue: (row, columnId) => {
        if (columnId === 'category') {
          const v = row.category;
          return v ? categoryOptions.find((o) => o.value === v)?.label ?? v : '';
        }
        if (columnId === 'account') {
          const v = row.account;
          return v ? accountOptions.find((o) => o.value === v)?.label ?? v : '';
        }
        if (columnId === 'status') {
          return statusOptions.find((o) => o.value === row.status)?.label ?? row.status;
        }
        if (columnId === 'opportunity_id') {
          if (!row.opportunity_id) return '';
          const opp = opportunities.find((o) => o.value === row.opportunity_id);
          return opp?.label ?? row.opportunity_id;
        }
        if (columnId === 'invoice_id') {
          if (!row.invoice_id) return row.invoice_id ?? '';
          const oppId = row.opportunity_id;
          const options = oppId ? invoiceOptionsByOpportunity[oppId] : [];
          const match = options?.find((o) => o.id === row.invoice_id);
          if (!match) return row.invoice_id;
          const prefix = match.invoice_number && match.invoice_total_count
            ? `(#${match.invoice_number} of ${match.invoice_total_count}) `
            : '';
          return `${prefix}${formatAppDate(match.issue_date)} — ${formatInvoiceAmount(match.total_amount, match.currency)}`;
        }
        if (columnId === 'amount') {
          const currency = row.currency || 'USD';
          return `${currency} ${row.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        return row[columnId as keyof Balance];
      },
      onOpenImport: openImportModal,
    },
  );

  const importModal = (
    <TableExcelImportModal
      opened={importModalOpened}
      onClose={() => setImportModalOpened(false)}
      onImport={handleAutoImport}
      file={autoImportFile}
      onFileChange={(file) => setAutoImportFile(file)}
      errors={importErrors}
      notification={notification}
    />
  );

  const handleRowDoubleClick = (rowData: Balance) => {
    setModalOpened(true);
    setEditingBalance(rowData);
    setBalanceForm({
      transaction_date: rowData.transaction_date,
      posting_date: rowData.posting_date,
      description: rowData.description || '',
      category: rowData.category,
      sub_category: rowData.sub_category || '',
      account: rowData.account,
      payment_method: rowData.payment_method || '',
      client_id: rowData.client_id,
      supplier_id: rowData.supplier_id,
      opportunity_id: rowData.opportunity_id,
      invoice_id: rowData.invoice_id,
      debit: rowData.debit,
      credit: rowData.credit,
      amount: rowData.amount,
      currency: rowData.currency || 'USD',
      status: rowData.status,
      notes: rowData.notes || '',
    });
    setIsEditingBalance(true);
    setErrorMessage('');
  };

  return (
    <>
      <Group position="apart" align="center" mb="md">
        <Group spacing="lg">
          <Title order={1}>{t('balance.title')}</Title>
          <InvoiceMonthFilter value={selectedMonth} onChange={setSelectedMonth} label={t('balance.transactionDate')}/>
        </Group>
        <Group>
          <Button variant="subtle" disabled={selectedMonth==='all'&&!statusFilter.length&&!clientFilter.length&&!opportunityFilter.length&&!categoryFilter.length&&!currencyFilter.length&&minFilter===''&&maxFilter===''&&!table.getState().globalFilter&&!table.getState().columnFilters.length}
            onClick={()=>{setSelectedMonth('all');setStatusFilter([]);setClientFilter([]);setOpportunityFilter([]);setCategoryFilter([]);setCurrencyFilter([]);setMinFilter('');setMaxFilter('');table.setGlobalFilter('');table.setColumnFilters([]);}}>{t('invoices.filters.clear')}</Button>
          <Button onClick={() => openModal()}>{t('balance.addBalance')}</Button>
        </Group>
      </Group>

      <PageRecordFilters fields={[
        {key:'status',label:t('common.status'),placeholder:t('invoices.filters.allStatuses'),options:statusOptions,value:statusFilter,onChange:setStatusFilter},
        {key:'client',label:t('balance.client'),placeholder:t('invoices.filters.allClients'),options:clients,value:clientFilter,onChange:setClientFilter},
        {key:'opportunity',label:t('balance.opportunity'),placeholder:t('common.recordFilters.allOpportunities'),options:opportunities,value:opportunityFilter,onChange:setOpportunityFilter},
        {key:'category',label:t('balance.category'),placeholder:t('common.recordFilters.allCategories'),options:categoryOptions,value:categoryFilter,onChange:setCategoryFilter},
        {key:'currency',label:t('balance.currency'),placeholder:t('common.recordFilters.allCurrencies'),options:filterCurrencyOptions,value:currencyFilter,onChange:value=>{setCurrencyFilter(value[0]==='all'?[]:value);setMinFilter('');setMaxFilter('');},single:true},
      ]} range={user?.role==='admin'?{min:rangeBounds.min,max:rangeBounds.max,value:[minFilter===''?rangeBounds.min:minFilter,maxFilter===''?rangeBounds.max:maxFilter],onChange:([min,max])=>{setMinFilter(min===rangeBounds.min?'':min);setMaxFilter(max===rangeBounds.max?'':max);},label:t('common.recordFilters.valueRange'),currency:currencyFilter[0]||'',disabled:currencyFilter.length!==1}:undefined}/>
      <div style={tableShellStyle}>
        <ExportableMantineReactTable table={table} exportModal={exportModal} importModal={importModal} />
      </div>

      <ImportConfirmModal
        opened={importConfirm != null}
        newCount={importConfirm?.newCount ?? 0}
        updateCount={importConfirm?.updateCount ?? 0}
        deleteCount={importConfirm?.deleteCount ?? 0}
        entityKey="balance"
        onCancel={() => setImportConfirm(null)}
        onConfirm={() => {
          const run = importConfirm?.run;
          setImportConfirm(null);
          void run?.();
        }}
      />

      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        centered
        title={
          editingBalance
            ? t('balance.editBalance')
            : t('balance.addNewBalance')
        }
        size="xl"
        styles={{ body: { padding: '0' } }}
      >
        <ScrollArea w="100%" h={modalScrollAreaHeight}>
          <div style={{ padding: '1rem' }}>
            <Group grow mb="sm">
              <DateInput
                label={t('balance.transactionDate')}
                value={
                  balanceForm.transaction_date
                    ? new Date(`${balanceForm.transaction_date}T00:00:00`)
                    : null
                }
                onChange={(e) =>
                  setBalanceForm({
                    ...balanceForm,
                    transaction_date: new Date(e as Date).toISOString().split('T')[0],
                  })
                }
                required
                popoverProps={{ withinPortal: true }}
              />
              <DateInput
                label={t('balance.postingDate')}
                value={
                  balanceForm.posting_date
                    ? new Date(`${balanceForm.posting_date}T00:00:00`)
                    : null
                }
                onChange={(e) =>
                  setBalanceForm({
                    ...balanceForm,
                    posting_date: e ? new Date(e as Date).toISOString().split('T')[0] : null,
                  })
                }
                popoverProps={{ withinPortal: true }}
                clearable
              />
            </Group>

            <TextInput
              label={t('balance.description')}
              placeholder={t('balance.descriptionPlaceholder')}
              value={balanceForm.description ?? ''}
              onChange={(e) =>
                setBalanceForm({ ...balanceForm, description: e.target.value })
              }
              mb="sm"
            />

            <Group grow mb="sm">
              <Select
                label={t('balance.category')}
                placeholder={t('common.select')}
                value={balanceForm.category ?? undefined}
                onChange={(value) =>
                  setBalanceForm({ ...balanceForm, category: value })
                }
                data={categoryOptions}
                clearable
                searchable
              />
              <TextInput
                label={t('balance.subCategory')}
                placeholder={t('balance.subCategoryPlaceholder')}
                value={balanceForm.sub_category ?? ''}
                onChange={(e) =>
                  setBalanceForm({ ...balanceForm, sub_category: e.target.value })
                }
              />
            </Group>

            <Group grow mb="sm">
              <Select
                label={t('balance.account')}
                placeholder={t('common.select')}
                value={balanceForm.account ?? undefined}
                onChange={(value) =>
                  setBalanceForm({ ...balanceForm, account: value })
                }
                data={accountOptions}
                clearable
                searchable
              />
              <TextInput
                label={t('balance.paymentMethod')}
                placeholder={t('balance.paymentMethodPlaceholder')}
                value={balanceForm.payment_method ?? ''}
                onChange={(e) =>
                  setBalanceForm({ ...balanceForm, payment_method: e.target.value })
                }
              />
            </Group>

            <Group grow mb="sm">
              <Select
                label={t('balance.client')}
                placeholder={t('balance.selectClient')}
                value={balanceForm.client_id ?? undefined}
                onChange={(value) => handleClientChange(value ?? null)}
                data={clients}
                clearable
                searchable
              />
              <Select
                label={t('balance.opportunity')}
                placeholder={balanceForm.client_id ? t('balance.selectOpportunity') : t('balance.selectClientFirst')}
                value={balanceForm.opportunity_id ?? undefined}
                onChange={(value) => handleOpportunityChange(value ?? null)}
                data={opportunitiesForClient}
                disabled={!balanceForm.client_id}
                clearable
                searchable
              />
            </Group>

            <Select
              label={t('balance.invoice')}
              placeholder={balanceForm.opportunity_id ? t('balance.selectInvoice') : t('balance.selectOpportunityFirst')}
              value={balanceForm.invoice_id ?? undefined}
              onChange={(value) => setBalanceForm((prev) => ({ ...prev, invoice_id: value ?? null }))}
              data={invoicesForOpportunity}
              clearable
              searchable
              mb="sm"
            />

            <Group grow mb="sm">
              <div>
                <NumberInput
                  label={t('balance.amount')}
                  description={maxAmountForInvoice != null ? t('balance.remainingForInvoice', { amount: maxAmountForInvoice.toFixed(2) }) : undefined}
                  value={typeof balanceForm.amount === 'number' ? balanceForm.amount : 0}
                  onChange={(value) =>
                    setBalanceForm({ ...balanceForm, amount: typeof value === 'number' ? value : 0 })
                  }
                  required
                  precision={2}
                  min={0}
                />
                {exceedsInvoiceRemaining && (
                  <Text size="xs" color="orange" mt={4}>
                    {t('balance.exceedsRemaining', { amount: maxAmountForInvoice!.toFixed(2) })}
                  </Text>
                )}
              </div>
              <Select
                label={t('balance.currency')}
                placeholder={t('common.select')}
                value={balanceForm.currency ?? 'USD'}
                onChange={(value) =>
                  setBalanceForm({ ...balanceForm, currency: value || 'USD' })
                }
                data={currencyOptions}
              />
              <Select
                label={t('common.status')}
                placeholder={t('common.select')}
                value={balanceForm.status ?? undefined}
                onChange={(value) =>
                  setBalanceForm({ ...balanceForm, status: value || 'pending' })
                }
                data={statusOptions}
              />
            </Group>

            <TextInput
              label={t('balance.notes')}
              placeholder={t('balance.notesPlaceholder')}
              value={balanceForm.notes ?? ''}
              onChange={(e) =>
                setBalanceForm({ ...balanceForm, notes: e.target.value })
              }
              mb="sm"
            />
          </div>
        </ScrollArea>

        <Group position="right" mt="md" style={{ margin: '1rem' }}>
          {isEditingBalance && (
            <Button
              onClick={deleteBalance}
              sx={{
                backgroundColor: 'rgba(163, 0, 0, 0.9)',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
              }}
            >
              {t('common.delete')}
            </Button>
          )}
          <Button
            onClick={() => {
              if (exceedsInvoiceRemaining) {
                setConfirmOverpayOpened(true);
                return;
              }
              (isEditingBalance ? updateBalance : createBalance)();
            }}
            disabled={!isFormValid()}
          >
            {isEditingBalance ? t('common.update') : t('common.save')}
          </Button>
        </Group>

        {(errorMessage || notification) && (
          <Group position="center" mt="md" mb="md">
            <Text className="error-message" color={notification?.color || 'red'}>
              {errorMessage || notification?.message}
            </Text>
          </Group>
        )}
      </Modal>

      <Modal
        opened={confirmOverpayOpened}
        onClose={() => setConfirmOverpayOpened(false)}
        centered
        title={t('balance.confirmOverpayTitle')}
        size="sm"
      >
        <Text size="sm" mb="md">
          {t('balance.confirmOverpayMessage', {
            entered: balanceForm.amount.toFixed(2),
            remaining: maxAmountForInvoice?.toFixed(2) ?? '0.00',
          })}
        </Text>
        <Group position="right">
          <Button variant="default" onClick={() => setConfirmOverpayOpened(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            color="orange"
            onClick={() => {
              setConfirmOverpayOpened(false);
              (isEditingBalance ? updateBalance : createBalance)();
            }}
          >
            {t('common.confirm')}
          </Button>
        </Group>
      </Modal>
    </>
  );
}
